import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Save, RefreshCw, PlayCircle, Loader2, CheckCircle2, AlertCircle, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";

// v1.43 — BK Centre UID Directory (Google Sheet synced).
// The WhatsApp bot asks BK Teachers for their Centre UID (masked format:
// five digits, then BK, then three digits). This panel points Medishiv at
// a 2-column Google Sheet (Centre UID, Centre) and refreshes the
// `bk_centres` collection so the bot can verify + guide typos in real time.
export default function BkCentresPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [testUid, setTestUid] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try { setCfg((await api.get(`/verification/bk-centres/config`)).data); }
    catch { toast.error("Failed to load config"); }
  };
  const loadRows = async () => {
    try {
      const { data } = await api.get(`/verification/bk-centres/list`,
        { params: { q: q || undefined, limit: 200 } });
      setRows(data.rows || []);
    } catch (err) {
      // Non-fatal — the panel still renders with empty rows; log so silent
      // failures don't hide behind a spinner.
      console.warn("bk-centres list load failed:", err);
    }
  };
  useEffect(() => { load(); loadRows(); }, []);
  useEffect(() => { const t = setTimeout(loadRows, 200); return () => clearTimeout(t); }, [q]);

  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/verification/bk-centres/config`, cfg);
      setCfg(data);
      toast.success("Configuration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const runSync = async (dry = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/verification/bk-centres/sync`, { dry_run: dry });
      toast.success(`${dry ? "Dry run" : "Sync"} complete — +${data.inserted}, ~${data.updated}, expired ${data.expired}, skipped ${data.skipped_bad_format}`);
      await load(); await loadRows();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const runVerify = async () => {
    try {
      const { data } = await api.post(`/verification/bk-centres/verify`, { uid: testUid });
      setTestResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
  };

  if (!cfg) return (
    <Card><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
      <Loader2 className="animate-spin" size={14}/> Loading…
    </CardContent></Card>
  );

  const last = cfg.last_sync_at ? new Date(cfg.last_sync_at).toLocaleString() : "never";
  const stats = cfg.last_sync_stats || {};
  const statusOk = (cfg.last_sync_status || "").startsWith("ok");

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <Building2 className="text-emerald-600" />
            <div>
              <div className="font-semibold">BK Centre UID Directory — Google Sheet sync</div>
              <p className="text-xs text-muted-foreground max-w-2xl">
                Paste a share-link to a Google Sheet with two columns —
                <b> Centre UID</b> (format <code className="text-[11px] bg-slate-100 px-1 rounded">*****/BK/***</code>, i.e. 5 digits / BK / 3 digits) and
                <b> Centre</b>. Medishiv pulls the list on a schedule and the WhatsApp bot verifies every teacher's UID
                against it, suggesting corrections for typos.
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => patch("enabled", v)} data-testid="bkc-enabled" />
              <span className="text-xs">{cfg.enabled ? "Auto-sync ON" : "Auto-sync OFF"}</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Google Sheet share URL</label>
              <Input value={cfg.sheet_url} onChange={(e) => patch("sheet_url", e.target.value)}
                     data-testid="bkc-sheet-url"
                     placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0" />
              <p className="text-[11px] text-slate-500 mt-1">
                Sheet must be shared as <b>Anyone with the link — Viewer</b> (or "Publish to web").
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">UID column</label>
                <Input value={cfg.uid_column} onChange={(e) => patch("uid_column", e.target.value)}
                       data-testid="bkc-uid-col" placeholder="Centre UID" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Name column</label>
                <Input value={cfg.name_column} onChange={(e) => patch("name_column", e.target.value)}
                       data-testid="bkc-name-col" placeholder="Centre" />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500">Auto-sync interval (min)</label>
              <Input type="number" min={1} value={cfg.sync_interval_minutes}
                     onChange={(e) => patch("sync_interval_minutes", parseInt(e.target.value || "15", 10))}
                     data-testid="bkc-interval" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Sheet ID (auto-detected)</label>
              <Input value={cfg.sheet_id || ""} disabled className="font-mono text-[11px]" />
            </div>
            <div>
              <label className="text-xs text-slate-500">gid</label>
              <Input value={cfg.gid || "0"} disabled className="font-mono text-[11px]" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="bkc-save"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => runSync(true)} disabled={syncing} data-testid="bkc-dry">
              <PlayCircle size={14}/> Dry run
            </Button>
            <Button size="sm" onClick={() => runSync(false)} disabled={syncing} data-testid="bkc-sync">
              {syncing ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>} Sync now
            </Button>
            {cfg.sheet_id && (
              <a href={`https://docs.google.com/spreadsheets/d/${cfg.sheet_id}/edit`}
                 target="_blank" rel="noreferrer"
                 className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 ml-2">
                <ExternalLink size={12}/> Open sheet
              </a>
            )}
            <div className="ml-auto flex items-center gap-2 text-xs">
              {statusOk
                ? <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 size={12} className="mr-1"/> OK</Badge>
                : (cfg.last_sync_status
                    ? <Badge className="bg-red-100 text-red-800"><AlertCircle size={12} className="mr-1"/> {cfg.last_sync_status.slice(0, 60)}</Badge>
                    : <Badge variant="outline">Not synced</Badge>)}
              <span className="text-slate-500">last: {last}</span>
            </div>
          </div>

          {stats && Object.keys(stats).length > 0 && (
            <div className="text-xs text-slate-600 grid grid-cols-2 md:grid-cols-5 gap-2 pt-1">
              <Stat k="Inserted" v={stats.inserted}/>
              <Stat k="Updated" v={stats.updated}/>
              <Stat k="Expired" v={stats.expired}/>
              <Stat k="Skipped (bad fmt)" v={stats.skipped_bad_format}/>
              <Stat k="Total active" v={stats.total_active}/>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <div className="font-semibold">Try a Centre UID</div>
            <span className="text-xs text-muted-foreground">— see exactly what the WhatsApp bot will reply.</span>
          </div>
          <div className="flex gap-2 items-center">
            <Input value={testUid} onChange={(e) => setTestUid(e.target.value)}
                   placeholder="*****/BK/***" data-testid="bkc-test-uid" className="max-w-xs font-mono" />
            <Button size="sm" onClick={runVerify} data-testid="bkc-test-verify">
              <Search size={14}/> Verify
            </Button>
          </div>
          {testResult && (
            <div className={`text-xs rounded-lg px-3 py-2 ${testResult.verified ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>
              {testResult.verified ? (
                <><CheckCircle2 size={12} className="inline mr-1"/> Verified — <b>{testResult.centre_uid}</b>{testResult.centre_name ? ` (${testResult.centre_name})` : ""}</>
              ) : (
                <><AlertCircle size={12} className="inline mr-1"/> {testResult.reason}</>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <div className="font-semibold">Directory ({rows.length})</div>
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search UID or centre name" data-testid="bkc-search" className="max-w-xs ml-auto" />
          </div>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-56">Centre UID</TableHead>
                  <TableHead>Centre</TableHead>
                  <TableHead className="w-52">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.centre_uid_norm} data-testid={`bkc-row-${r.centre_uid_norm}`}>
                    <TableCell className="font-mono text-xs">{r.centre_uid}</TableCell>
                    <TableCell className="text-sm">{r.centre_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                    No rows yet — save the sheet URL and hit <b>Sync now</b>.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase text-slate-500">{k}</div>
      <div className="font-semibold">{v ?? "—"}</div>
    </div>
  );
}
